import { InputTextField, InputTextFieldProps } from '@almbrand/inputtextfield';
import { ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { useContext, useEffect, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormResetField, UseFormSetValue } from 'react-hook-form';
import { store } from 'store/store';
import { mapBBRDataToInputValue } from '../../../services/BBRMappingService';
import styles from './InputTextFieldComponent.module.scss';

const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
const numberRegex = /\D/g;
export interface InputTextFieldComponentProps extends InputTextFieldProps<ThemeUnion> {
	register: UseFormRegister<FieldValues>;
	resetField?: UseFormResetField<FieldValues>;
	contentType: string;
	stepContentId: number;
	isValid: boolean;
	hasError?: boolean;
	stepTitle?: string;
	setValue?: UseFormSetValue<FieldValues>;
}

export const InputTextFieldComponent: React.FC<InputTextFieldComponentProps> = ({
	register,
	stepContentId,
	contentType,
	resetField,
	hasError,
	setValue,
	...props
}) => {
	const pageContext = useContext(PageContext);
	const { theme, metaArea } = pageContext ?? {};
	const themeName = ThemeNames[theme];

	const [hasValidateError, setHasValidateError] = useState(hasError);
	const updatedComponentProps = {
		...props,
		themeName: themeName,
	};

	const { onBlur, onChange, name } = register(stepContentId.toString(), { required: props.required });

	const invalidateField = () => {
		resetField(name);
		updatedComponentProps.isValid = false;
		setHasValidateError(true);
	};

	const isValid = (value) => {
		let length;
		switch (updatedComponentProps.type) {
			case 'number':
				if (value >= (updatedComponentProps.min as number) && value <= (updatedComponentProps.max as number)) {
					return true;
				}
				return false;
			case 'tel':
				length = value?.replaceAll(numberRegex, '')?.length;
				return length >= 8 && length <= 10;

			case 'text':
				if (
					value.length >= (updatedComponentProps.min as number) &&
					value.length <= (updatedComponentProps.max as number)
				) {
					return true;
				}
				return false;
			case 'email':
				return value?.match(emailRegex) !== null;

			default:
				return value?.length > 0;
		}
	};

	const handleOnBlur = (evt) => {
		if (!isValid(evt.target.value)) {
			invalidateField();
			return;
		}
		setHasValidateError(false);
		updatedComponentProps.isValid = true;
		onBlur({
			target: {
				name,
				value: {
					id: name,
					value: evt.target?.value,
					type: contentType,
					stepTitle: props.stepTitle,
					inputLabel: props.label,
				},
			},
		});
	};

	const handleOnChange = (evt) => {
		if (!isValid(evt.target.value)) {
			if (updatedComponentProps.isValid) {
				invalidateField();
			}
		} else {
			setHasValidateError(false);
			updatedComponentProps.isValid = true;
			onChange({
				target: {
					name,
					value: {
						id: name,
						value: evt.target?.value,
						type: contentType,
						stepTitle: props.stepTitle,
						inputLabel: props.label,
					},
				},
			});
		}
	};

	useEffect(() => {
		if (!hasValidateError) {
			setHasValidateError(hasError);
		}
	}, [hasError]);

	const data = store.getState().calculatorForm[name]?.data;

	useEffect(() => {
		if (data?.value) {
			setValue(name, {
				id: name,
				value: data.value,
				type: contentType,
				stepTitle: props.stepTitle,
				inputLabel: props.label,
			});
		}
	}, [data]);

	return (
		<div className={styles.InputTextFieldComponent}>
			<InputTextField
				{...updatedComponentProps}
				onBlur={handleOnBlur}
				onChange={handleOnChange}
				hasError={hasValidateError}
				value={mapBBRDataToInputValue(data, metaArea) || ''}
			/>
		</div>
	);
};
